/**
 * Presentational component for handoff audio controls.
 *
 * Renders a mute-toggle button and a leave button.
 * All interactive elements have `data-testid` attributes (AGENT-CONVENTIONS.md).
 * Status is communicated with both icon and text — never color only.
 * Accessible labels are explicit so screen readers announce the action correctly.
 *
 * This component has NO dependency on the LiveKit SDK. It is a pure presentational
 * component driven by the state values from `useHandoffRoom`.
 */

import type React from 'react';
import type { HandoffRoomState } from '../types.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface HandoffAudioControlsProps {
  /** Current room state — determines button enabling. */
  state: HandoffRoomState;
  /** Whether the local microphone is currently muted. */
  muted: boolean;
  /** Callback to mute the microphone. */
  onMute: () => void;
  /** Callback to un-mute the microphone. */
  onUnmute: () => void;
  /** Callback to leave the room. */
  onLeave: () => void;
  /** Optional additional class names for the container. */
  className?: string | undefined;
}

// ---------------------------------------------------------------------------
// Inline SVG icons (thin-line, 24×24, round caps, 1.3 stroke — brand spec)
// No icon library dependency in this isolated package.
// ---------------------------------------------------------------------------

function MicOnIcon(): React.ReactElement {
  return (
    <svg
      aria-hidden="true"
      focusable={false}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 2a4 4 0 0 1 4 4v6a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="8" y1="22" x2="16" y2="22" />
      <path d="M19 10a7 7 0 0 1-14 0" />
    </svg>
  );
}

function MicOffIcon(): React.ReactElement {
  return (
    <svg
      aria-hidden="true"
      focusable={false}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V6a3 3 0 0 0-5.94-.6" />
      <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
      <line x1="12" y1="19" x2="12" y2="22" />
      <line x1="8" y1="22" x2="16" y2="22" />
    </svg>
  );
}

function LeaveIcon(): React.ReactElement {
  return (
    <svg
      aria-hidden="true"
      focusable={false}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.42 19.42 0 0 1 4.43 9.88a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 3.54 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11z" />
      <line x1="23" y1="1" x2="17" y2="7" />
      <polyline points="17 1 23 1 23 7" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ACTIVE_STATES: ReadonlySet<HandoffRoomState> = new Set<HandoffRoomState>(['connected']);

/**
 * Renders mute-toggle and leave controls for a handoff room.
 * Buttons are disabled when the room is not in the 'connected' state.
 */
export function HandoffAudioControls({
  state,
  muted,
  onMute,
  onUnmute,
  onLeave,
  className,
}: HandoffAudioControlsProps): React.ReactElement {
  const isConnected = ACTIVE_STATES.has(state);

  const handleMuteToggle = (): void => {
    if (muted) {
      onUnmute();
    } else {
      onMute();
    }
  };

  const muteLabel = muted ? 'Unmute microphone' : 'Mute microphone';

  return (
    <div
      data-testid="handoff-audio-controls"
      role="toolbar"
      aria-label="Call audio controls"
      className={className}
      style={{ display: 'flex', gap: '12px', alignItems: 'center' }}
    >
      {/* Mute / unmute toggle */}
      <button
        type="button"
        data-testid="handoff-mute-btn"
        aria-label={muteLabel}
        aria-pressed={muted}
        disabled={!isConnected}
        onClick={handleMuteToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          cursor: isConnected ? 'pointer' : 'not-allowed',
          opacity: isConnected ? 1 : 0.5,
        }}
      >
        {muted ? <MicOffIcon /> : <MicOnIcon />}
        {/* Text is always shown — no color-only status (AGENT-CONVENTIONS.md) */}
        <span>{muted ? 'Unmute' : 'Mute'}</span>
      </button>

      {/* State label — shown when not connected */}
      {!isConnected && (
        <span
          data-testid="handoff-state-label"
          aria-live="polite"
          role="status"
          style={{ fontSize: '0.875rem' }}
        >
          {stateToLabel(state)}
        </span>
      )}

      {/* Leave room */}
      <button
        type="button"
        data-testid="handoff-leave-btn"
        aria-label="Leave call"
        disabled={!isConnected}
        onClick={onLeave}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          cursor: isConnected ? 'pointer' : 'not-allowed',
          opacity: isConnected ? 1 : 0.5,
        }}
      >
        <LeaveIcon />
        <span>Leave</span>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function stateToLabel(state: HandoffRoomState): string {
  switch (state) {
    case 'idle':
      return 'Not connected';
    case 'connecting':
      return 'Connecting…';
    case 'mic_denied':
      return 'Microphone access denied';
    case 'token_denied':
      return 'Unable to join — token rejected';
    case 'disconnected':
      return 'Disconnected';
    case 'failed':
      return 'Connection failed';
    case 'connected':
      return 'Connected';
  }
}
